import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, X } from 'lucide-react';

export interface Toast {
  id: number;
  title: string;
  body?: string;
  tone: 'info' | 'success';
  icon?: ReactNode;
}

interface ToastContextValue {
  toast: (toast: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const toast = useCallback((entry: Omit<Toast, 'id'>) => {
    const id = nextId.current++;
    setToasts((current) => [...current.slice(-2), { ...entry, id }]);
    window.setTimeout(() => dismiss(id), 4200);
  }, [dismiss]);

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((entry) => (
          <div key={entry.id} className={`toast toast-${entry.tone}`}>
            <span className="toast-icon" aria-hidden="true">
              {entry.icon ?? (entry.tone === 'success' ? <CheckCircle2 size={17} /> : <Info size={17} />)}
            </span>
            <div className="toast-copy">
              <b>{entry.title}</b>
              {entry.body && <p>{entry.body}</p>}
            </div>
            <button onClick={() => dismiss(entry.id)} aria-label="Dismiss notification"><X size={14} /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside ToastProvider.');
  return value;
}
